/**
 * @file EnhancedCodeEditorPanel.jsx
 * @description Monaco-powered code editor panel for the problem-solving workspace.
 *              Provides multi-language support (JS, Python, Java, C++), syntax highlighting,
 *              keyboard shortcuts (Ctrl+Enter to run, Ctrl+Shift+Enter to submit),
 *              code copy/reset/download, last accepted submission loading, and
 *              an auth-gated LoginOverlay for unauthenticated users.
 *
 * @module components/EnhancedCodeEditorPanel
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Editor, { useMonaco } from '@monaco-editor/react';
import { apiGet } from '../utils/apiUtils';
import {
    FiPlay,
    FiSend,
    FiRotateCcw,
    FiChevronDown,
    FiCopy,
    FiCheck,
    FiSettings,
    FiSun,
    FiMoon,
    FiDownload,
    FiClock,
    FiLock,
    FiUnlock
} from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { motion } from 'framer-motion';

/**
 * Overlay component displayed when an unauthenticated user attempts to view the code editor.
 * Provides a quick sign-in CTA (Google OAuth).
 * 
 * @param {Object} props
 * @param {Function} props.navigate - Navigation function.
 * @returns {JSX.Element}
 */
const LoginOverlay = ({ navigate }) => {
    const { signInWithGoogle } = useAuth();
    return (
        <div className="absolute inset-0 bg-neutral-800 backdrop-blur-sm flex items-center justify-center z-[100]">
            <div className="p-8 text-center max-w-md mx-4">
                <div className="mb-3">
                    <h3 className="text-xl flex items-center justify-center space-x-2 font-semibold text-gray-400 mb-2">
                        Code Editor is Locked
                        <FiLock className="text-lg text-gray-400 flex items-center justify-center ml-2" />
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                        Sign in to start solving problems...
                    </p>
                </div>
                <div className="space-y-1">
                    <button
                        onClick={signInWithGoogle}
                        className="w-full px-6 py-3 text-gray-400 hover:text-white font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                    >
                        <FcGoogle className="text-lg" />
                        <span>Sign in with Google</span>
                    </button>
                </div>
            </div>
        </div>
    );
};



/**
 * Core Code Editor component powered by Monaco Editor.
 * Provides features like syntax highlighting, multi-language support, font scaling,
 * dark/light themes, code downloading, and cooldowns for run/submit buttons.
 * 
 * @param {Object} props - Component properties.
 * @param {string} props.code - The current code string.
 * @param {Function} props.setCode - State updater for the code string.
 * @param {string} props.language - The currently selected language (e.g., 'python').
 * @param {Function} props.setLanguage - State updater for the selected language.
 * @param {Object} props.languageMap - Mapping of language names to IDs for the backend.
 * @param {boolean} props.loadingTemplate - Whether the boilerplate template is currently loading.
 * @param {boolean} props.isRunning - Whether a code execution (Run) is in progress.
 * @param {boolean} props.isSubmitting - Whether a code submission (Submit) is in progress.
 * @param {Function} props.onRunTests - Callback triggered when the 'Run' button is clicked.
 * @param {Function} props.onSubmit - Callback triggered when the 'Submit' button is clicked.
 * @param {Function} props.onReset - Callback triggered to reset code to boilerplate.
 * @param {number|string} props.questionId - The ID of the current coding problem.
 * @param {boolean} props.isSubmissionLoading - Whether the component is fetching the last submission.
 * @param {boolean} [props.canRun=true] - Whether the 'Run' button should be enabled.
 * @param {boolean} [props.canSubmit=true] - Whether the 'Submit' button should be enabled.
 * @param {number} [props.runCooldown=0] - Cooldown timer (in seconds) for the Run button.
 * @param {number} [props.submitCooldown=0] - Cooldown timer (in seconds) for the Submit button.
 * @param {Function} props.navigate - React Router navigation function.
 * @returns {JSX.Element}
 */
const EnhancedCodeEditorPanel = ({
    code, setCode, language, setLanguage, languageMap, loadingTemplate,
    isRunning, isSubmitting, onRunTests, onSubmit, onReset, questionId,
    isSubmissionLoading, canRun = true, canSubmit = true, runCooldown = 0, submitCooldown = 0,
    navigate
}) => {
    // ─── COMMENTED OUT: Batch-admin-only gating ───────────────────────────
    // To restore editor access only for batch admins, uncomment the lines
    // below and also uncomment the <ComingSoonOverlay> rendering in the JSX.
    //
    // const IS_COMING_SOON = true;
    // const [isBatchAdmin, setIsBatchAdmin] = useState(false);
    // const [checkingAdmin, setCheckingAdmin] = useState(true);
    // const showComingSoon = IS_COMING_SOON && !isBatchAdmin && !checkingAdmin;
    //
    // useEffect(() => {
    //     const checkAdminStatus = async () => {
    //         if (!isAuthenticated) {
    //             setCheckingAdmin(false);
    //             return;
    //         }
    //         try {
    //             const response = await apiGet('/batches/is-global-admin');
    //             if (response.ok) {
    //                 const data = await response.json();
    //                 setIsBatchAdmin(!!data.isGlobalAdmin);
    //             }
    //         } catch (error) {
    //             console.error('Error fetching admin status:', error);
    //         } finally {
    //             setCheckingAdmin(false);
    //         }
    //     };
    //     checkAdminStatus();
    // }, [isAuthenticated]);
    // ─── END: Batch-admin-only gating ───────────────────────────────────

    const { isAuthenticated, loading } = useAuth();

    const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
    const [copied, setCopied] = useState(false);
    const [theme, setTheme] = useState('vs-dark');
    const [fontSize, setFontSize] = useState(14);
    const [showSettings, setShowSettings] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [loadingLastSubmission, setLoadingLastSubmission] = useState(false);
    const [lastSubmissionStatus, setLastSubmissionStatus] = useState(null);
    const [monacoInstance, setMonacoInstance] = useState(null);

    const editorRef = useRef(null);

    const monacoLanguages = {
        'javascript': 'javascript',
        'python': 'python',
        'java': 'java',
        'cpp': 'cpp'
    };

    const themes = [
        { id: 'vs-dark', name: 'Dark', icon: FiMoon },
        { id: 'vs-light', name: 'Light', icon: FiSun }
    ];

    const editorOptions = {
        fontSize: fontSize,
        fontFamily: 'Fira Code, Monaco, Menlo, "Ubuntu Mono", monospace',
        fontLigatures: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        automaticLayout: true,
        lineNumbers: 'on',
        folding: false,
        lineNumbersMinChars: 3,
        lineDecorationsWidth: 16,
        renderLineHighlight: 'all',
        selectOnLineNumbers: true,
        bracketPairColorization: { enabled: true },
        guides: {
            bracketPairs: false,
            indentation: true
        },
        suggestOnTriggerCharacters: isAuthenticated,
        acceptSuggestionOnCommitCharacter: isAuthenticated,
        tabCompletion: isAuthenticated ? 'on' : 'off',
        parameterHints: { enabled: isAuthenticated },
        quickSuggestions: isAuthenticated,
        contextmenu: isAuthenticated,
        mouseWheelZoom: true,
        formatOnPaste: isAuthenticated,
        formatOnType: isAuthenticated,
        readOnly: !isAuthenticated,
        domReadOnly: !isAuthenticated,
        cursorStyle: isAuthenticated ? 'line' : 'block',
        glyphMargin: false
    };

    const handleEditorDidMount = useCallback((editor, monaco) => {
        editorRef.current = editor;
        setMonacoInstance(monaco);

        if (isAuthenticated) {
            editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
                if (canRun && !isRunning && !isSubmitting) {
                    onRunTests();
                }
            });

            editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.Enter, () => {
                if (canSubmit && !isRunning && !isSubmitting) {
                    onSubmit();
                }
            });

            editor.addCommand(monaco.KeyMod.Alt | monaco.KeyMod.Shift | monaco.KeyCode.KeyF, () => {
                formatCode();
            });
        }


        monaco.editor.setTheme('vs-dark');
    }, [isAuthenticated, canRun, canSubmit, isRunning, isSubmitting, onRunTests, onSubmit]);

    useEffect(() => {
        if (isSubmissionLoading.current) {
            isSubmissionLoading.current = false;
            return;
        }
        if (language) {
            onReset();
        }
    }, [language]);

    const monaco = useMonaco();

    useEffect(() => {
        if (monaco) {
            monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
                noSemanticValidation: true,
                noSyntaxValidation: true,
            });

            monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
                target: monaco.languages.typescript.ScriptTarget.ESNext,
                allowNonTsExtensions: true,
            });
        }
    }, [monaco]);

    useEffect(() => {
        if (editorRef.current && monacoInstance) {
            const model = editorRef.current.getModel();
            if (model) {
                monacoInstance.editor.setModelLanguage(model, monacoLanguages[language]);
            }
        }
    }, [language, monacoInstance]);

    useEffect(() => {
        const handleMonacoErrors = (event) => {
            const error = event.error || event.reason;

            if (error && (
                error.name === 'AbortError' ||
                error.type === 'cancelation' ||
                error.msg === 'operation is manually canceled' ||
                (error.message && (
                    error.message.includes('canceled') ||
                    error.message.includes('Monaco') ||
                    error.message.includes('editor')
                ))
            )) {
                event.preventDefault();
                return;
            }
        };

        window.addEventListener('error', handleMonacoErrors);
        window.addEventListener('unhandledrejection', handleMonacoErrors);

        return () => {
            window.removeEventListener('error', handleMonacoErrors);
            window.removeEventListener('unhandledrejection', handleMonacoErrors);
        };
    }, []);

    const loadLastSubmission = async () => {
        if (!isAuthenticated) {
            return;
        }

        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) return;

        setLoadingLastSubmission(true);
        setLastSubmissionStatus(null);

        try {
            const currentLanguageId = languageMap[language].id;
            const response = await apiGet(`/auth/submissions/${user.id}?questionId=${questionId}&status=Accepted&limit=1&language=${currentLanguageId}`);

            if (response.ok) {
                const data = await response.json();

                if (data.submissions && data.submissions.length > 0) {
                    const submission = data.submissions[0];

                    const languageName = Object.keys(languageMap).find(
                        key => languageMap[key].id === submission.language_id
                    );

                    isSubmissionLoading.current = true;

                    if (languageName) {
                        setLanguage(languageName);
                    }

                    setCode(submission.source_code);
                    setLastSubmissionStatus({
                        status: submission.status,
                        date: new Date(submission.created_at).toLocaleDateString()
                    });
                } else {
                    setLastSubmissionStatus({
                        status: 'No accepted submission',
                        date: null
                    });
                }
            } else if (response.status === 404) {
                setLastSubmissionStatus({
                    status: 'No accepted submission',
                    date: null
                });
            } else {
                throw new Error('Failed to load submission');
            }
        } catch (error) {
            setLastSubmissionStatus({
                status: 'Error loading',
                date: null
            });
        } finally {
            setLoadingLastSubmission(false);
        }
    };

    const copyToClipboard = async () => {
        if (!isAuthenticated || !editorRef.current) return;

        try {
            const code = editorRef.current.getValue();
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
        }
    };

    const formatCode = async () => {
        if (!isAuthenticated || !editorRef.current) return;
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const handleRunTestsInFullscreen = () => {
        if (!isAuthenticated) {
            navigate('/signin');
            return;
        }

        if (isFullscreen) {
            setIsFullscreen(false);
            setTimeout(() => {
                onRunTests();
            }, 100);
        } else {
            onRunTests();
        }
    };

    const handleSubmitInFullscreen = () => {
        if (!isAuthenticated) {
            navigate('/signin');
            return;
        }

        if (isFullscreen) {
            setIsFullscreen(false);
            setTimeout(() => {
                onSubmit();
            }, 100);
        } else {
            onSubmit();
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.language-selector')) {
                setShowLanguageDropdown(false);
            }
            if (!event.target.closest('.settings-panel')) {
                setShowSettings(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    return (
        <div className={`flex flex-col bg-neutral-800 ${isFullscreen ? 'fixed inset-0 z-50' : 'h-full'} relative`}>
            {/* Login Overlay for unauthenticated users */}
            {!isAuthenticated && <LoginOverlay navigate={navigate} />}

            <div className="bg-neutral-800 px-2 border-b border-neutral-700">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <div className="flex space-x-1 hidden md:flex">
                            <div className={`w-3 h-3 rounded-full bg-red-500`}></div>
                            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        </div>
                        <span className="text-sm text-gray-400 font-mono">
                            main.{languageMap[language]?.ext}
                        </span>
                    </div>

                    <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={handleRunTestsInFullscreen}
                                disabled={!isAuthenticated || !canRun || isRunning || isSubmitting || loadingTemplate}
                                className={`px-2 py-1 text-gray-400 hover:text-white text-sm font-medium transition-all duration-200 flex items-center space-x-1.5 rounded ${!isAuthenticated || !canRun || isRunning || isSubmitting || loadingTemplate
                                    ? 'bg-neutral-700 cursor-not-allowed'
                                    : ''
                                    }`}
                                title={
                                    !isAuthenticated ? 'Sign in to run code' :
                                        !canRun ? `Wait ${runCooldown}s before running again` :
                                            isRunning ? 'Running tests...' :
                                                isSubmitting ? 'Submitting...' :
                                                    'Run Tests (Ctrl+Enter)'
                                }
                            >
                                {!isAuthenticated ? (
                                    <FiLock className="text-sm" />
                                ) : !canRun ? (
                                    <FiClock className="text-sm" />
                                ) : (
                                    <FiPlay className="text-sm" />
                                )}
                                <span className='hidden md:block'>{!isAuthenticated ? 'Sign In' : !canRun ? `${runCooldown}s` : 'Run'}</span>
                            </button>

                            <button
                                onClick={handleSubmitInFullscreen}
                                disabled={!isAuthenticated || !canSubmit || isRunning || isSubmitting || loadingTemplate}
                                className={`px-2 py-1 text-gray-400 hover:text-white text-sm font-medium transition-all duration-200 flex items-center space-x-1.5 rounded ${!isAuthenticated || !canSubmit || isRunning || isSubmitting || loadingTemplate
                                    ? 'bg-neutral-700 cursor-not-allowed'
                                    : ''
                                    }`}
                                title={
                                    !isAuthenticated ? 'Sign in to submit solution' :
                                        !canSubmit ? `Wait ${submitCooldown}s before submitting again` :
                                            isRunning ? 'Running tests...' :
                                                isSubmitting ? 'Submitting...' :
                                                    'Submit Solution (Ctrl+Shift+Enter)'
                                }
                            >
                                {!isAuthenticated ? (
                                    <FiLock className="text-sm" />
                                ) : !canSubmit ? (
                                    <FiClock className="text-sm" />
                                ) : (
                                    <FiSend className="text-sm" />
                                )}
                                <span className='hidden md:block'>{!isAuthenticated ? 'Sign In' : !canSubmit ? `${submitCooldown}s` : 'Submit'}</span>
                            </button>

                            <div className="h-6 w-[1px] bg-gray-400 mx-2" />

                            <button
                                onClick={copyToClipboard}
                                disabled={!isAuthenticated || loadingTemplate}
                                className="p-1.5 text-gray-400 hover:text-white disabled:opacity-50 rounded transition-all duration-200"
                                title={isAuthenticated ? "Copy Code (Ctrl+C)" : "Sign in to copy code"}
                            >
                                {copied ? <FiCheck className="text-green-400" /> : <FiCopy />}
                            </button>

                            <button
                                onClick={onReset}
                                disabled={!isAuthenticated || loadingTemplate}
                                className="p-1.5 text-gray-400 hover:text-white disabled:opacity-50 rounded transition-all duration-200"
                                title={isAuthenticated ? "Reset to Template" : "Sign in to reset code"}
                            >
                                <FiRotateCcw />
                            </button>

                            <button
                                onClick={loadLastSubmission}
                                disabled={!isAuthenticated || loadingLastSubmission || loadingTemplate}
                                className="p-1.5 text-gray-400 hover:text-white disabled:opacity-50 rounded transition-all duration-200"
                                title={isAuthenticated ? "Load Last Accepted Submission" : "Sign in to load submissions"}
                            >
                                <FiDownload className="text-sm" />
                            </button>

                            {/* <div className="relative settings-panel">
                                <button
                                    onClick={() => isAuthenticated && setShowSettings(!showSettings)}
                                    disabled={!isAuthenticated || loadingTemplate}
                                    className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-50 rounded transition-all duration-200"
                                    title={isAuthenticated ? "Editor Settings" : "Sign in to change settings"}
                                >
                                    <FiSettings />
                                </button>
                                {showSettings && (
                                    <div className="absolute right-0 top-full mt-1 bg-white border border-border rounded shadow-lg z-50 p-3 min-w-[200px]">
                                        <div className="space-y-3">
                                            <div>
                                                <label className="text-xs text-muted-foreground block mb-1">Font Size</label>
                                                <input
                                                    type="range"
                                                    min="12"
                                                    max="20"
                                                    value={fontSize}
                                                    onChange={(e) => setFontSize(parseInt(e.target.value))}
                                                    className="w-full"
                                                />
                                                <span className="text-xs text-muted-foreground">{fontSize}px</span>
                                            </div>

                                            <div>
                                                <label className="text-xs text-muted-foreground block mb-2">Theme</label>
                                                <div className="flex space-x-1">
                                                    {themes.map((themeOption) => {
                                                        const Icon = themeOption.icon;
                                                        return (
                                                            <button
                                                                key={themeOption.id}
                                                                onClick={() => setTheme(themeOption.id)}
                                                                className={`p-2 rounded flex items-center space-x-1 text-xs ${theme === themeOption.id
                                                                    ? 'bg-emerald-600 text-white'
                                                                    : 'bg-gray-100 text-foreground'
                                                                    }`}
                                                            >
                                                                <Icon />
                                                                <span>{themeOption.name}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div> */}

                            <div className="h-6 w-[1px] bg-gray-400 mx-2" />

                            <div className="flex items-center space-x-1 language-selector">
                                {Object.entries(languageMap).map(([key, lang]) => {
                                    const isActive = language === key;
                                    return (
                                        <button
                                            key={key}
                                            onClick={() => isAuthenticated && !loadingTemplate && setLanguage(key)}
                                            disabled={!isAuthenticated || loadingTemplate}
                                            className="group relative p-1.5 transition-all duration-300 rounded disabled:opacity-50"
                                            title={lang.name}
                                        >
                                            <img
                                                src={`/languages/${lang.logo}`}
                                                alt={lang.name}
                                                className={`w-8 h-8 object-contain transition-colors duration-300 ${isActive
                                                    ? 'grayscale-0 px-0.5'
                                                    : 'grayscale opacity-100 px-0.5'
                                                    }`}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 relative overflow-hidden font-mono">
                <Editor
                    height="100%"
                    language={monacoLanguages[language] || 'plaintext'}
                    value={code}
                    theme="vs-dark"
                    onChange={(value) => setCode(value || '')}
                    onMount={handleEditorDidMount}
                    options={editorOptions}
                    loading={
                        <div className="flex items-center justify-center h-full bg-[#0d1117]">
                            <div className="text-center">
                                <div className="w-10 h-10 border-4 border-rose-500/20 border-t-rose-600 rounded-full animate-spin mx-auto mb-4"></div>
                                <p className="text-slate-400 text-sm font-medium">Environment Setup...</p>
                            </div>
                        </div>
                    }
                />

                {loadingTemplate && (
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center">
                        <div className="text-center">
                            <div className="w-10 h-10 border-4 border-rose-500/20 border-t-rose-600 rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-white text-sm font-medium">Loading Template...</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EnhancedCodeEditorPanel;